import type { ArtifactItem } from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import { ScrollArea } from '@eriko/ui/src/components/ui/scroll-area';
import { useResizableArtifact } from '@eriko/ui/src/hooks/useResizableArtifact';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from 'lucide-react';
import { ArtifactContent } from './ArtifactContent';

interface ArtifactPanelViewProps {
  artifacts: ArtifactItem[];
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
  storagePrefix: string;
}

export function ArtifactPanelView({
  artifacts,
  activeIndex,
  setActiveIndex,
  storagePrefix,
}: ArtifactPanelViewProps) {
  const { width, isMaximized, onHandlePointerDown, toggleMaximize } =
    useResizableArtifact(storagePrefix);

  if (activeIndex === null || !artifacts[activeIndex]) return null;

  const activeArtifact = artifacts[activeIndex];
  const total = artifacts.length;
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < total - 1;

  return (
    <div
      className="relative flex h-full flex-col border-l bg-background"
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize artifact panel"
        onPointerDown={onHandlePointerDown}
        className="absolute left-0 top-0 z-10 h-full w-1 cursor-col-resize hover:bg-surface-200 active:bg-surface-300"
      />
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Details</h2>
          {total > 1 && (
            <span className="text-xs text-muted-foreground">
              {activeIndex + 1} of {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {total > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!hasPrev}
                onClick={() => setActiveIndex(activeIndex - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!hasNext}
                onClick={() => setActiveIndex(activeIndex + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={isMaximized ? 'Restore panel width' : 'Maximize panel'}
            onClick={toggleMaximize}
          >
            {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setActiveIndex(null)}
            className="h-7 w-7"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-4">
        <ArtifactContent data={activeArtifact.data} />
      </ScrollArea>
    </div>
  );
}
